import './kitStyles';
import React, { useEffect, useId, useRef } from 'react';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries';
import { IconButton } from './IconButton';
import { isKeyboardInput, msSinceKeyboardChange } from './inputMode';
import { isPhone } from './popRules';

/**
 * WEB-3310 юнит 2б-1 — окно кита, эталон `Modal({ open, onClose, title,
 * children, footer, width, testid, side, dismiss })`
 * (`design/cabinet-rescin/kit.jsx:1056–1083`, кит 02:553–594).
 *  - Настоящий `<dialog>`: `open` из `false` в `true` — `showModal()` и отметка
 *    времени открытия; из `true` в `false` — класс `out` и `close()` через 200 мс
 *    (плавное закрытие); закрытие по `open=false` `onClose` не зовёт.
 *  - Esc (`cancel`) — то же плавное закрытие; `onClose` — по событию `close`, если
 *    окно было открыто по `open`.
 *  - Щелчок мимо (по самому `dialog` вне его прямоугольника) закрывает при
 *    `dismiss` и на телефоне всегда, но не в первые 450 мс после открытия
 *    (щелчок того же касания, что открыло окно).
 *  - `busy` — идёт запрос окна (WEB-3760, защита WEB-617): Esc и щелчок мимо не
 *    закрывают (ни на телефоне, ни с `dismiss`), крестик погашен; `open=false`
 *    закрывает как обычно. Без `busy` — всё прежнее. Закрытие браузером (неотменяемый
 *    Esc, граница пункта `noEsc`) при `busy` тоже отменяется — окно снова открыто,
 *    `onClose` не зван (WEB-4072: иначе «Новый проект» невидим, а `open` у хозяина
 *    остаётся `true`). Значение `busy` — текущее, через ссылку.
 *  - `sticky` — щелчок мимо не закрывает НИКОГДА: ни на телефоне, ни с `dismiss`
 *    (WEB-3757 возврат 1: окно, которое нельзя потерять случайным касанием, — значение
 *    секрета показывается один раз). Esc и кнопки закрывают как обычно. По умолчанию
 *    выключено — остальные окна ведут себя по-прежнему.
 *  - `noEsc` — Esc не закрывает (отмена браузера гасится, окно остаётся открытым);
 *    закрыть можно только кнопкой окна. WEB-3757 юнит 4, возврат 1: полное значение
 *    ключа доступа показывается один раз, одно нажатие Esc теряло его безвозвратно.
 *    По умолчанию выключено — Esc закрывает, как раньше.
 *    Граница (возврат 2): гасить `cancel` браузер разрешает не всегда — нажатие Esc
 *    без действий человека между нажатиями (Chromium 149: третье подряд) приходит
 *    неотменяемым, и браузер закрывает окно сам. Поэтому окно помечает СВОИ закрытия
 *    (`shut` — Esc без `noEsc`, крестик, щелчок мимо, `open=false`), а закрытие не
 *    от них при `noEsc` (и при `busy`) отменяет: снова `showModal()`, `onClose` не зовётся. Свой
 *    немодальный `show()` с подложкой не взят: пропали бы верхний слой, `::backdrop`,
 *    инертность страницы и ловушка фокуса — у этого окна они те же, что у прочих.
 *  - Крестик «Закрыть» — только без подвала и не у боковой (`side` — класс
 *    `drawer`).
 *  - Открыто не с клавиатуры — фокус на самом окне (клавиатура телефона не
 *    выезжает, ободка нет); с клавиатуры — фокус не перехватывается.
 *  - Высота меняется плавно (220 мс), кроме боковой, `prefers-reduced-motion` и
 *    «печатают» (фокус в поле внутри окна) и меньше 700 мс после смены высоты
 *    клавиатуры или видимой высоты (`msSinceKeyboardChange()`, ставит
 *    `PhoneRules`, юнит 3; эталон `kit.jsx:1072`).
 * Глобальных `window.__w2aKbd`, `window.__kbAt` нет — признак клавиатуры
 * `isKeyboardInput()` и отметка смены клавиатуры (`ds/inputMode.ts`). Имя совпадает с antd — где нужны оба,
 * импорт с переименованием.
 */
export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  title: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  testid?: string;
  side?: boolean;
  dismiss?: boolean;
  /** Идёт запрос: Esc и щелчок мимо не закрывают, крестик погашен (WEB-3760). */
  busy?: boolean;
  /** Не закрывать щелчком мимо окна — ни на телефоне, ни с `dismiss`. Esc закрывает. */
  sticky?: boolean;
  /** Не закрывать клавишей Esc — только кнопкой окна. По умолчанию Esc закрывает. */
  noEsc?: boolean;
}

/** Время затухания окна при закрытии; его же ждёт `useLingerAfterClose` окон доменов (WEB-3754). */
export const MODAL_OUT_MS = 200;
const OPEN_GUARD_MS = 450;
const TYPING = 'input, textarea, [contenteditable=true]';
const KB_SETTLE_MS = 700;

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  testid,
  side,
  dismiss,
  busy,
  sticky,
  noEsc,
}: ModalProps): React.ReactElement {
  const common = useStrings(commonStrings);
  const ref = useRef<HTMLDialogElement>(null);
  const hid = 'mh-' + useId().replace(/:/g, '');
  const openRef = useRef(open);
  openRef.current = open;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const outTimer = useRef<ReturnType<typeof setTimeout>>();
  // своё закрытие (через `shut`) — не путать с закрытием браузером мимо кнопок окна
  const selfClose = useRef(false);
  const noEscRef = useRef(noEsc);
  noEscRef.current = noEsc;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  const shut = () => {
    const d = ref.current;
    if (!d || !d.open || d.classList.contains('out')) return;
    d.classList.add('out');
    outTimer.current = setTimeout(() => {
      d.classList.remove('out');
      if (d.open) {
        selfClose.current = true;
        d.close();
      }
    }, MODAL_OUT_MS);
  };

  // Снятое окно не закрываем таймером: он срабатывал бы на узле вне страницы.
  useEffect(() => () => clearTimeout(outTimer.current), []);

  useEffect(() => {
    const d = ref.current;
    if (!d || !d.isConnected) return;
    if (open && !d.open) {
      d.classList.remove('out');
      d.showModal();
      d.dataset.openAt = String(performance.now());
      if (!isKeyboardInput()) {
        d.tabIndex = -1;
        d.focus({ preventScroll: true });
      }
    }
    if (!open && d.open) shut();
  }, [open]);

  useEffect(() => {
    const d = ref.current;
    if (!d || side || typeof ResizeObserver === 'undefined') return undefined;
    let h = 0;
    let anim: Animation | null = null;
    const ro = new ResizeObserver(() => {
      if (!d.open) {
        h = 0;
        return;
      }
      if (anim) return;
      const n = d.offsetHeight;
      const a = document.activeElement;
      const typing = (!!a && d.contains(a) && a.matches(TYPING)) || msSinceKeyboardChange() < KB_SETTLE_MS;
      const still = typeof window.matchMedia === 'function' && !!window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
      if (h && !typing && Math.abs(n - h) > 2 && !still && typeof d.animate === 'function') {
        const run = d.animate([{ height: h + 'px' }, { height: n + 'px' }], {
          duration: 220,
          easing: 'cubic-bezier(.2, .8, .2, 1)',
        });
        anim = run;
        run.onfinish = run.oncancel = () => {
          anim = null;
          h = d.offsetHeight;
        };
      }
      h = n;
    });
    ro.observe(d);
    return () => ro.disconnect();
  }, [side]);

  const onClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    const d = ref.current;
    if (!d || e.target !== d || sticky || !(dismiss || isPhone())) return;
    if (busy) return;
    if (performance.now() - Number(d.dataset.openAt || 0) < OPEN_GUARD_MS) return;
    const r = d.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) shut();
  };

  return (
    <dialog
      ref={ref}
      className={'modal' + (side ? ' drawer' : '')}
      onClose={() => {
        const own = selfClose.current;
        selfClose.current = false;
        const d = ref.current;
        const hold = noEscRef.current || busyRef.current;
        if (!own && hold && openRef.current && d && d.isConnected && !d.open) {
          // закрыл браузер (неотменяемый Esc) — окно, которое нельзя потерять (`noEsc`)
          // или с идущим запросом (`busy`), остаётся
          d.showModal();
          d.dataset.openAt = String(performance.now());
          return;
        }
        if (openRef.current && closeRef.current) closeRef.current();
      }}
      onCancel={(e) => {
        e.preventDefault();
        if (busy) return;
        if (!noEsc) shut();
      }}
      onClick={onClick}
      aria-labelledby={hid}
      data-testid={testid || 'modal'}
      style={width ? { width: 'min(' + width + 'px, calc(100vw - 32px))' } : undefined}
    >
      <div className="mh" id={hid}>
        {title}
        {!footer && !side && (
          <IconButton icon="close" size="sm" label={common.close} onClick={shut} className="mh-x" disabled={busy} />
        )}
      </div>
      <div className="mb">{children}</div>
      {footer && <div className="mf">{footer}</div>}
    </dialog>
  );
}
